import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GraphqlResolverInput } from 'global';
import { useDebounce } from 'hooks/useDebounce';
import { Listing } from 'components/shared/TableListing';
import { useFormContext, useWatch } from 'react-hook-form';
import { CalendarEventsListingInput } from '@trnsact/trnsact-shared-types';

import { BulkActions } from './BulkActions';
import { CalendarMapper } from '../../mapper';
import { ListingFiltersValues } from './types';
import { eventsListingColumns } from './eventsListingColumns';
import { calendarApi, CalendarEventsListingResponse } from '../../api';

const PER_PAGE = 25;

interface Props {
  userProfileId?: string | null;
}

export const EventsListing = ({ userProfileId }: Props) => {
  const { control } = useFormContext<ListingFiltersValues>();
  const { search, ...filters } = useWatch({ control, name: 'filters' });

  const debouncedSearch = useDebounce(search.trim());

  const queryFilters = {
    ...(filters.type.length ? { type: filters.type } : {}),
    ...(filters.status.length ? { status: filters.status } : {}),
    ...(filters.priority.length ? { priority: filters.priority } : {}),
    ...(filters.dateRange?.from || filters.dateRange?.to ? { dateRange: filters.dateRange } : {}),
  };

  // Any change of search/filters starts over from the first page; deriving the page during render
  // avoids a request with the stale page that a reset in useEffect would cause.
  const filtersKey = JSON.stringify({ debouncedSearch, queryFilters });
  const [pageState, setPageState] = useState({ filtersKey, page: 1 });
  const page = pageState.filtersKey === filtersKey ? pageState.page : 1;

  const { loading, data, previousData } = useQuery<
    CalendarEventsListingResponse,
    GraphqlResolverInput<CalendarEventsListingInput>
  >(calendarApi.events.queries.listing, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        pagination: { page, perPage: PER_PAGE },
        search: debouncedSearch,
        ...(userProfileId && { userProfileId }),
        filters: queryFilters,
      },
    },
  });

  const items = data?.dealActivity?.result?.items ?? previousData?.dealActivity?.result?.items ?? [];
  const tableData = items.map(CalendarMapper.toEventListingRow);

  return (
    <Listing
      selection={{}}
      loading={loading}
      tableData={tableData}
      columns={eventsListingColumns}
      pagination={{
        page,
        perPage: PER_PAGE,
        total: data?.dealActivity?.result?.pagination?.total ?? 0,
        onChange: nextValue => setPageState({ filtersKey, page: nextValue }),
      }}
      bulkActions={({ selected, onClose }) => <BulkActions selectedIds={selected} onClose={onClose} />}
    />
  );
};
